
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Navbar from './components/Navbar';
import Background from './components/Background';
import ArtCard from './components/ArtCard';
import CustomRequest from './components/CustomRequest';
import HeroCarousel from './components/HeroCarousel';
import ImageModal from './components/ImageModal';
import MouseParticles from './components/MouseParticles';
import AdminPanel from './components/AdminPanel';
import AdminLogin from './components/AdminLogin';
import UserAuthModal from './components/UserAuthModal';
import SaveIndicator from './components/SaveIndicator';
import UserOrders from './components/UserOrders';
import UserProfile from './components/UserProfile';
import ChatOverlay from './components/ChatOverlay';
import CommentsSection from './components/CommentsSection';
import { ProfileScreenshotsCarousel } from './components/ProfileScreenshotsCarousel';
import { Footer } from './components/Footer';
import { FaqSection } from './components/FaqSection';
import { database } from './services/databaseService';
import { storage } from './services/storageService';
import { ArtItem, CartItem, ArtCategory, Language, Currency, Order, OrderStatus, ChatThread, ChatMessage, Comment, User, CommissionTier, SiteSettings, ProfileScreenshot } from './types';
import { INITIAL_ARTS, DATA_VERSION, DEFAULT_CATEGORY_COVERS, DEFAULT_COMMISSION_TIERS, DEFAULT_COMMENTS, ART_SUBCATEGORIES, ART_TYPES } from './constants';
import { slugify, getActiveDiscountPercentage, formatCurrency } from './utils';
import { Flower, Settings, ArrowRight, Loader2, Search, Trash2, X, ShoppingBag, CheckCircle, CloudDownload, Sparkles, RefreshCw, Server, ShieldCheck, Lock, Unlock, PlayCircle, LayoutGrid, ChevronLeft, ChevronRight, ArrowUpDown, Calendar, DollarSign, ArrowUp, ArrowDown } from 'lucide-react';

const App: React.FC = () => {
  // Função para pegar a página inicial baseada no Hash ou Path da URL
  const getInitialPageFromHash = () => {
    try {
      const pathParts = window.location.pathname.replace(/^\//, '').split('/');
      if (pathParts[0] === 'art') return 'art';

      const hash = window.location.hash.replace('#', '');
      const page = hash.split('/')[0];
      const validPages = ['home', 'gallery', 'custom-art', 'faq', 'my-orders', 'profile', 'art'];
      return validPages.includes(page) ? page : 'home';
    } catch (e) {
      return 'home';
    }
  };

  const [activePage, setActivePage] = useState(getInitialPageFromHash());
  const [arts, setArts] = useState<ArtItem[]>([]); 
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [chatThreads, setChatThreads] = useState<ChatThread[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedArt, setSelectedArt] = useState<ArtItem | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<ArtCategory | null>(() => {
    try {
      const hash = window.location.hash.replace('#', '');
      const page = hash.split('/')[0];
      return page === 'gallery' ? ArtCategory.ANIMATED : null;
    } catch (e) {
      return null;
    }
  });
  const [subCategoryFilter, setSubCategoryFilter] = useState<string | null>(null);
  const [artTypeFilter, setArtTypeFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'price' | 'default'>('default');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryCovers, setCategoryCovers] = useState<Record<string, string>>(DEFAULT_CATEGORY_COVERS);
  const [commissionTiers, setCommissionTiers] = useState<CommissionTier[]>([]);
  const [profileScreenshots, setProfileScreenshots] = useState<ProfileScreenshot[]>([]);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    customRequestIntro_pt: 'Deseja algo que transcenda a galeria comum? Entre em contato com nossos mestres artesãos para solicitar uma arte personalizada para o seu perfil.',
    customRequestIntro_en: 'Looking for something beyond the common gallery? Contact our master craftsmen to request a personalized art for your profile.',
    customRequestIntro_es: '¿Buscas algo más allá de la galería común? Contacta con nuestros maestros artesanos para solicitar un arte personalizado para tu perfil.',
    customRequestIntro_ru: 'Ищете нечто большее, чем обычная галерея? Свяжитесь с нашими мастерами для заказа персонального арта для вашего профиля.'
  });
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem('niiko_language');
      return (saved === 'pt' || saved === 'en' || saved === 'es' || saved === 'ru') ? saved : 'en';
    } catch {
      return 'en';
    }
  });
  const [currency, setCurrency] = useState<Currency>(() => {
    try {
      const saved = localStorage.getItem('niiko_currency');
      return (saved === 'USD' || saved === 'BRL' || saved === 'EUR' || saved === 'RUB') ? saved : 'USD';
    } catch {
      return 'USD';
    }
  });

  const handleLanguageChange = (newLang: Language) => {
    setLanguage(newLang);
    try {
      localStorage.setItem('niiko_language', newLang);
    } catch {}
  };

  const handleCurrencyChange = (newCurr: Currency) => {
    setCurrency(newCurr);
    try {
      localStorage.setItem('niiko_currency', newCurr);
    } catch {}
  };
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<'preparing' | 'redirecting' | null>(null);
  const [checkoutRedirectUrl, setCheckoutRedirectUrl] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isUserAuthOpen, setIsUserAuthOpen] = useState(false);
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [isAdminLoginOpen, setIsAdminLoginOpen] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'cloud' | 'local'>('local');
  const [notifications, setNotifications] = useState({ orders: 0, chats: 0 });
  const artsGridRef = useRef<HTMLDivElement>(null);
  const subCategoriesRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const initialPageForRef = getInitialPageFromHash();
  const previousPageRef = useRef<string>(initialPageForRef === 'art' ? 'gallery' : initialPageForRef);

  // Guarda a página anterior a 'art' para retornar a ela ao fechar a arte expandida
  useEffect(() => {
    if (activePage !== 'art') {
      previousPageRef.current = activePage;
    }
  }, [activePage]);

  const activeDiscount = React.useMemo(() => {
    return getActiveDiscountPercentage(siteSettings);
  }, [siteSettings]);

  const smoothScrollToRef = (targetRef: React.RefObject<HTMLDivElement | null>, offset = 110) => {
    if (!targetRef.current) return;
    const elementPosition = targetRef.current.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - offset;
    
    // Custom buttery-smooth scroll transition with cubic ease-in-out
    const startY = window.scrollY;
    const difference = offsetPosition - startY;
    const duration = 1000; // 1 second duration for a premium, majestic gliding feel
    const startTime = performance.now();

    const easeInOutCubic = (t: number): number => {
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    };

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = easeInOutCubic(progress);

      window.scrollTo(0, startY + difference * ease);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  const handleCategoryChange = (cat: ArtCategory | null) => {
    const nextCat = categoryFilter === cat ? null : cat;
    setCategoryFilter(nextCat);
    setSubCategoryFilter(null);
    setArtTypeFilter(null);
    setTimeout(() => {
      if (nextCat) {
        smoothScrollToRef(subCategoriesRef, 110);
      } else {
        smoothScrollToRef(artsGridRef, 110);
      }
    }, 120);
  };

  const handleSubCategoryChange = (subId: string | null) => {
    setSubCategoryFilter(subId);
    setTimeout(() => {
      smoothScrollToRef(artsGridRef, 110);
    }, 120);
  };

  const handleArtTypeChange = (typeId: string | null) => {
    setArtTypeFilter(typeId);
    setTimeout(() => {
      smoothScrollToRef(artsGridRef, 110);
    }, 120);
  };

  const handleAddComment = async (text: string, rating: number) => {
    if (!currentUser) return;
    
    const newComment: Comment = {
      id: 'REV-' + Date.now(),
      userName: currentUser.name,
      userEmail: currentUser.email,
      text: text,
      date: new Date().toISOString().split('T')[0],
      rating: rating,
      verified: true
    };

    setComments(prev => [newComment, ...prev]);
    await database.saveComment(newComment);
  };

  // Monitora mudanças na URL (Botão voltar/avançar do navegador com popstate e hashchange)
  useEffect(() => {
    const handleLocationChange = () => {
      // 1. Verificar se a URL atual é /art/:id (pathname)
      const pathParts = window.location.pathname.replace(/^\//, '').split('/');
      let targetArtId = '';
      if (pathParts[0] === 'art' && pathParts[1]) {
        targetArtId = decodeURIComponent(pathParts[1]);
      }

      // 2. Ou se tem hash #art/:id ou #gallery/:id
      if (!targetArtId) {
        const hash = window.location.hash.replace('#', '');
        if (hash.includes('/')) {
          const [page, artId] = hash.split('/');
          if ((page === 'gallery' || page === 'art') && artId) {
            targetArtId = decodeURIComponent(artId);
          }
        }
      }

      if (targetArtId) {
        if (arts.length > 0) {
          const art = arts.find(a => a.id === targetArtId);
          if (art) {
            setSelectedArt(art);
            setActivePage('art');
            return;
          }
        }
      } else if (selectedArt) {
        // Se a URL não tem mais arte selecionada, fecha o modal
        setSelectedArt(null);
      }

      const newPage = getInitialPageFromHash();
      if (newPage === 'gallery' && activePage !== 'gallery' && activePage !== 'art') {
        setCategoryFilter(ArtCategory.ANIMATED);
        setSubCategoryFilter(null);
        setArtTypeFilter(null);
      }
      setActivePage(newPage);
    };

    window.addEventListener('hashchange', handleLocationChange);
    window.addEventListener('popstate', handleLocationChange);
    return () => {
      window.removeEventListener('hashchange', handleLocationChange);
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, [arts, selectedArt, activePage]);

  // Listen for typing sequence '4708' to trigger Admin Login
  useEffect(() => {
    let keysPressed = '';
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore shortcut if user is typing in interactive form fields
      const activeEl = document.activeElement;
      if (activeEl && (
        activeEl.tagName === 'INPUT' || 
        activeEl.tagName === 'TEXTAREA' || 
        activeEl.tagName === 'SELECT' ||
        activeEl.getAttribute('contenteditable') === 'true'
      )) {
        return;
      }

      keysPressed += e.key.toLowerCase();
      if (keysPressed.length > 4) {
        keysPressed = keysPressed.slice(-4);
      }

      if (keysPressed === '4708') {
        e.preventDefault();
        setIsAdminLoginOpen(true);
        keysPressed = '';
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Sincronizar selectedArt com a URL (Pathname, Query param ou Hash)
  useEffect(() => {
    if (isLoaded && arts.length > 0) {
      let targetArtId = '';

      // 1. Checa pathname /art/ID ou /art/ID/slug
      const pathParts = window.location.pathname.replace(/^\//, '').split('/');
      if (pathParts[0] === 'art' && pathParts[1]) {
        targetArtId = decodeURIComponent(pathParts[1]);
      }

      // 2. Checa query param ?art=ID
      if (!targetArtId) {
        const params = new URLSearchParams(window.location.search);
        const qArt = params.get('art');
        if (qArt) targetArtId = decodeURIComponent(qArt);
      }

      // 3. Checa hash #art/ID ou #gallery/ID (retrocompatibilidade)
      if (!targetArtId) {
        const hash = window.location.hash.replace('#', '');
        if (hash.includes('/')) {
          const [page, artId] = hash.split('/');
          if ((page === 'gallery' || page === 'art') && artId) {
            targetArtId = decodeURIComponent(artId);
          }
        }
      }

      if (targetArtId) {
        const lowerTarget = targetArtId.toLowerCase();
        const art = arts.find(a => 
          a.id === targetArtId || 
          a.id.toLowerCase() === lowerTarget ||
          (a.title && (
            (a.title.en && slugify(a.title.en) === lowerTarget) ||
            (a.title.pt && slugify(a.title.pt) === lowerTarget) ||
            (a.title.en && a.title.en.toLowerCase() === lowerTarget) ||
            (a.title.pt && a.title.pt.toLowerCase() === lowerTarget)
          ))
        );
        if (art) {
          const sanitized = database.processArtItem(art);
          setSelectedArt(sanitized);
          setActivePage('art');
          // Se a URL tinha hash #art/..., atualiza para a URL limpa /art/ID sem o #
          const targetPath = `/art/${art.id}`;
          if (window.location.hash.startsWith('#art/') || window.location.pathname !== targetPath) {
            window.history.replaceState({ artId: art.id }, '', targetPath);
          }
        }
      }
    }
  }, [isLoaded, arts]);

  // Atualiza a URL e o título quando uma arte é selecionada ou fechada
  useEffect(() => {
    if (!isLoaded) return;
    
    const currentHash = window.location.hash.replace('#', '');
    const [page] = currentHash.split('/');
    
    if (selectedArt) {
      const targetPath = `/art/${selectedArt.id}`;
      // Atualiza a barra de endereço para /art/ID (idêntica ao botão de compartilhar, sem '#')
      if (window.location.pathname !== targetPath || window.location.hash) {
        window.history.pushState({ artId: selectedArt.id }, '', targetPath);
        setActivePage('art');
      }
      const title = (selectedArt.title[language] || selectedArt.title.en || selectedArt.title.pt || 'Artwork') + ' - Niiko Artwork';
      document.title = title;
    } else {
      document.title = 'Niiko Artwork - Violet & Ink';
      // Se fechou a arte e a URL do navegador ainda é /art/ID ou tem hash de arte
      if (window.location.pathname.startsWith('/art/')) {
        const returnPage = previousPageRef.current && previousPageRef.current !== 'art' && previousPageRef.current !== 'home'
          ? `/#${previousPageRef.current}`
          : '/';
        window.history.pushState(null, '', returnPage);
        setActivePage(previousPageRef.current || 'gallery');
      } else if (page === 'art' || currentHash.startsWith('art/')) {
        const returnPage = previousPageRef.current || 'gallery';
        window.location.hash = returnPage;
        setActivePage(returnPage);
      }
    }
  }, [selectedArt, isLoaded, language]);

  // Salva a posição do scroll anterior ao abrir uma arte
  useEffect(() => {
    if (selectedArt && activePage !== 'art') {
      scrollPositionRef.current = window.scrollY;
    }
  }, [selectedArt, activePage]);

  // Sempre rola a janela para o topo quando muda de página (exceto para rota da arte para manter o scroll do usuário, ou restaurando o scroll anterior ao fechar a arte)
  useEffect(() => {
    if (activePage !== 'art') {
      if (scrollPositionRef.current > 0) {
        window.scrollTo({ top: scrollPositionRef.current, behavior: 'auto' });
        scrollPositionRef.current = 0;
      } else {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    }
  }, [activePage]);

  const loadInitialCache = useCallback(async () => {
    let hasArtsCache = false;
    try {
      // 1. Carrega todas as informações locais do IndexedDB (Ultra-rápido, ~15ms)
      const [
        cachedArts,
        cachedCovers,
        cachedTiers,
        cachedSettings,
        cachedComments,
        cachedOrders,
        cachedChats,
        cachedShots,
        user
      ] = await Promise.all([
        storage.load('arts'),
        storage.load('category_covers', 'registry'),
        storage.load('commission_tiers', 'registry'),
        storage.load('settings', 'registry'),
        storage.load('comments'),
        storage.load('orders'),
        storage.load('chats'),
        storage.load('profile_screenshots', 'registry'),
        database.getCurrentUser()
      ]);

      // 2. Define o usuário atual
      if (user) setCurrentUser(user);

      // 3. Processa e aplica as artes locais de imediato
      if (cachedArts && cachedArts.length > 0) {
        const deleted: string[] = await storage.load('deleted_arts', 'registry') || [];
        const filteredCached = cachedArts.filter((a: any) => !deleted.includes(a.id));
        const processedCached = filteredCached.map((art: any) => database.processArtItem(art));
        setArts(processedCached);
        hasArtsCache = true;
      } else {
        setArts(INITIAL_ARTS.map(art => database.processArtItem(art)));
      }

      // 4. Capas de Categorias do cache
      if (cachedCovers) {
        const resolved: Record<string, string> = { ...DEFAULT_CATEGORY_COVERS };
        Object.entries(cachedCovers).forEach(([cat, path]) => {
          if (path) {
            resolved[cat] = database.resolveMediaUrl(path as string);
          }
        });
        setCategoryCovers(resolved);
      }

      // 5. Tiers de comissão do cache
      if (cachedTiers && cachedTiers.length > 0) {
        const resolvedTiers = cachedTiers.map((t: any) => ({
          ...t,
          demoUrl: database.resolveMediaUrl(t.demoUrl)
        }));
        setCommissionTiers(resolvedTiers);
      } else {
        setCommissionTiers(DEFAULT_COMMISSION_TIERS.map(t => ({
          ...t,
          demoUrl: database.resolveMediaUrl(t.demoUrl)
        })));
      }

      // 6. Configurações do site do cache
      if (cachedSettings) {
        setSiteSettings(cachedSettings);
      } else {
        try {
          const ls = localStorage.getItem('niiko_site_settings_backup');
          if (ls) {
            const parsed = JSON.parse(ls);
            if (parsed && typeof parsed === 'object') setSiteSettings(parsed);
          }
        } catch (e) {}
      }

      // 7. Avaliações/Comentários do cache
      if (cachedComments && cachedComments.length > 0) {
        const cleanComments = cachedComments
          .filter((c: any) => c && c.id && !c.id.startsWith('SEED-'))
          .filter((comment: any, index: number, self: any[]) => self.findIndex(c => c.id === comment.id) === index);
        setComments(cleanComments);
      }

      // 8. Pedidos do cache
      if (cachedOrders && cachedOrders.length > 0) {
        const deleted: string[] = await storage.load('deleted_orders', 'registry') || [];
        const filteredOrders = cachedOrders.filter((o: any) => !deleted.includes(o.id));
        setOrders(filteredOrders);
      }

      // 9. Chats do cache
      if (cachedChats && cachedChats.length > 0) {
        setChatThreads(cachedChats);
      }

      // 10. Screenshots de perfis do cache
      if (cachedShots && cachedShots.length > 0) {
        setProfileScreenshots(cachedShots);
      } else {
        try {
          const ls = localStorage.getItem('niiko_profile_screenshots_backup');
          if (ls) {
            const parsed = JSON.parse(ls);
            if (Array.isArray(parsed) && parsed.length > 0) setProfileScreenshots(parsed);
          }
        } catch (e) {}
      }
    } catch (e) {
      console.warn("Falha ao carregar cache inicial:", e);
      setArts(INITIAL_ARTS.map(art => database.processArtItem(art)));
    } finally {
      // Se já temos artes no cache local, unbloqueamos o loader imediatamente!
      if (hasArtsCache) {
        setIsLoaded(true);
      }
    }
    return hasArtsCache;
  }, []);

  const syncData = useCallback(async (hasArtsCache: boolean) => {
    setIsSyncing(true);
    try {
      // Registrar visita (Analytics) - Fire-and-forget para não bloquear o carregamento principal!
      database.logVisit().catch(err => console.warn("Analytics log failed", err));

      // 1. Carrega as Artes Atuais (Prioridade Máxima!)
      // Desacoplado das outras requisições para carregar instantaneamente, sem esperar nada!
      database.getArts().then((artsRes) => {
        if (artsRes && artsRes.data) {
          setArts(artsRes.data);
          setSyncStatus(artsRes.source === 'hostinger_server' ? 'cloud' : 'local');
        }
      }).catch(err => {
        console.error("Error syncing arts:", err);
      }).finally(() => {
        // Se NÃO tínhamos cache (novo usuário), esperamos a resposta das artes atuais antes de fechar o loader
        if (!hasArtsCache) {
          setIsLoaded(true);
        }
      });

      // 2. Carrega as Outras Informações Essenciais de forma silenciosa e paralela
      database.getCategoryCovers().then((covers) => {
        if (covers) setCategoryCovers(covers);
      }).catch(err => console.error("Error syncing covers:", err));

      database.getCommissionTiers().then((tiers) => {
        if (tiers) setCommissionTiers(tiers);
      }).catch(err => console.error("Error syncing tiers:", err));

      database.getSettings().then((settings) => {
        if (settings) setSiteSettings(settings);
      }).catch(err => console.error("Error syncing settings:", err));

      database.getProfileScreenshots().then((shots) => {
        if (shots && shots.length > 0) setProfileScreenshots(shots);
      }).catch(err => console.error("Error syncing profile screenshots:", err));

      // 3. Dados do Usuário conectados sob demanda (Pedidos e Chats só se houver login ativo)
      database.getCurrentUser().then((user) => {
        if (user) {
          setCurrentUser(user);
          // Só solicita pedidos e chats do servidor Hostinger se houver usuário autenticado!
          database.getOrders().then((ordersRes) => {
            if (ordersRes && ordersRes.data) setOrders(ordersRes.data);
          }).catch(err => console.error("Error syncing orders:", err));

          database.getChatThreads().then((chatsRes) => {
            if (chatsRes) setChatThreads(chatsRes);
          }).catch(err => console.error("Error syncing chats:", err));
        }
      }).catch(err => console.error("Error checking user session:", err));

      // Sincroniza usuários existentes com o servidor em segundo plano
      database.getAllUsers().catch(() => {});

      // 4. Carrega os Comentários/Avaliações
      database.getComments().then((commRes) => {
        if (commRes && commRes.data && commRes.data.length > 0) {
          const cleanComments = commRes.data
            .filter(c => c && c.id && !c.id.startsWith('SEED-'))
            .filter((comment, index, self) => self.findIndex(c => c.id === comment.id) === index);
          setComments(cleanComments);
          if (cleanComments.length !== commRes.data.length) {
            database.saveAllComments(cleanComments).catch(err => console.warn(err));
          }
        }
      }).catch(err => console.error("Error syncing comments:", err));

      // Timeout de segurança se as artes falharem para novos visitantes não travarem infinitamente
      if (!hasArtsCache) {
        setTimeout(() => {
          setIsLoaded(true);
        }, 4000);
      }

    } catch (e) {
      console.error("Background sync failure:", e);
      if (!hasArtsCache) {
        setIsLoaded(true);
      }
    } finally {
      setIsSyncing(false);
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      const hasCache = await loadInitialCache();
      syncData(hasCache);
    };
    run();
  }, [loadInitialCache, syncData]);

  useEffect(() => {
    // Evita chamadas de polling pesadas no servidor se for apenas um visitante anônimo
    if (!currentUser && !isAdminPanelOpen) return;

    const fetchImmediately = async () => {
      try {
        const [chats, ordersRes] = await Promise.all([
          database.getChatThreads(),
          database.getOrders()
        ]);
        
        if (chats) {
          setChatThreads(prev => {
            const isFreshLoad = prev.length === 0;
            if (chats.length > prev.length && !isFreshLoad) {
              setNotifications(n => ({ ...n, chats: n.chats + (chats.length - prev.length) }));
            } else if (!isFreshLoad) {
              // Check if any thread has new messages
              chats.forEach(newThread => {
                const oldThread = prev.find(t => t.userEmail === newThread.userEmail);
                if (oldThread && newThread.messages.length > oldThread.messages.length) {
                  const lastMsg = newThread.messages[newThread.messages.length - 1];
                  if (lastMsg.sender === 'user') {
                    setNotifications(n => ({ ...n, chats: n.chats + 1 }));
                  }
                }
              });
            }
            return chats;
          });
        }

        if (ordersRes && ordersRes.data) {
          setOrders(prev => {
            const isFreshLoad = prev.length === 0;
            if (ordersRes.data.length > prev.length && !isFreshLoad) {
              setNotifications(n => ({ ...n, orders: n.orders + (ordersRes.data.length - prev.length) }));
            }
            return ordersRes.data;
          });
        }
      } catch (err) {
        console.warn("Immediate polling call failed:", err);
      }
    };

    fetchImmediately();

    const interval = setInterval(fetchImmediately, 10000);
    return () => clearInterval(interval);
  }, [currentUser, isAdminPanelOpen]);

  useEffect(() => {
    const total = notifications.orders + notifications.chats;
    const prefix = total > 0 ? `(${total}) ` : '';
    
    // Dynamic Title & Description
    let title = 'Niiko Artwork - Steam Profile Art Design';
    let description = language === 'en' 
      ? 'Custom Steam profile design, animated backgrounds, avatars, and workshop showcases. Elevate your Steam profile with elite anime artwork!'
      : 'Design de perfil Steam personalizado, fundos animados, avatares e destaques de oficina. Eleve seu perfil Steam com arte anime de elite!';

    if (selectedArt) {
      const artTitle = selectedArt.title[language] || selectedArt.title['en'] || 'Steam Artwork';
      const cat = selectedArt.category || 'Steam Showcase';
      title = `${artTitle} | ${cat} - Niiko Artwork`;
      description = language === 'en'
        ? `Check out "${artTitle}" - high quality animated Steam ${cat.toLowerCase()} designed by Niiko. Elevate your Steam profile with this customized anime artwork!`
        : `Veja "${artTitle}" - ${cat.toLowerCase()} animada de alta qualidade para perfil Steam desenhada por Niiko. Melhore seu perfil com esta arte personalizada!`;
    } else if (activePage === 'custom-art') {
      title = language === 'en' 
        ? 'Order Custom Steam Art | Commission Showcase - Niiko Artwork'
        : 'Encomendar Arte Personalizada Steam | Comissões de Destaques - Niiko Artwork';
      description = language === 'en'
        ? 'Order your dream custom animated Steam profile art, workshop showcases, and custom illustrations directly from Niiko.'
        : 'Encomende a arte de perfil do Steam animada dos seus sonhos, destaques de oficina e ilustrações personalizadas diretamente com Niiko.';
    } else if (activePage === 'gallery') {
      title = language === 'en'
        ? 'Steam Artwork Gallery | Animated Showcases - Niiko Artwork'
        : 'Galeria de Artes Steam | Destaques Animados - Niiko Artwork';
      description = language === 'en'
        ? 'Explore our extensive gallery of animated Steam profile arts, workshop showcases, and elite gaming designs.'
        : 'Explore nossa extensa galeria de artes animadas para perfil do Steam, destaques de oficina e designs gamers de elite.';
    } else if (activePage === 'faq') {
      title = language === 'en'
        ? 'F.A.Q. & Support | Help Center - Niiko Artwork'
        : 'F.A.Q. & Suporte | Central de Ajuda - Niiko Artwork';
    }

    document.title = `${prefix}${title}`;

    // Update Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    // Dynamic JSON-LD Structured Data for Google SEO
    let jsonLdScript = document.getElementById('seo-jsonld') as HTMLScriptElement | null;
    if (!jsonLdScript) {
      jsonLdScript = document.createElement('script');
      jsonLdScript.id = 'seo-jsonld';
      jsonLdScript.type = 'application/ld+json';
      document.head.appendChild(jsonLdScript);
    }

    const structuredData: any = {
      "@context": "https://schema.org",
      "@type": selectedArt ? "VisualArtwork" : "WebSite",
      "name": selectedArt ? (selectedArt.title[language] || selectedArt.title['en']) : "Niiko Artwork",
      "url": window.location.href,
      "description": description,
    };

    if (selectedArt) {
      structuredData.image = selectedArt.imageUrl;
      structuredData.artMedium = "Digital";
      structuredData.artform = "Digital Animation";
      structuredData.creator = {
        "@type": "Person",
        "name": "Niiko"
      };
      structuredData.offers = {
        "@type": "Offer",
        "price": selectedArt.price,
        "priceCurrency": language === 'en' ? 'USD' : 'BRL',
        "availability": "https://schema.org/InStock"
      };
    } else {
      structuredData.potentialAction = {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${window.location.origin}/#gallery?search={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      };
    }

    try {
      jsonLdScript.text = JSON.stringify(structuredData);
    } catch (e) {
      console.warn("Failed to stringify JSON-LD", e);
    }

  }, [notifications, activePage, selectedArt, language]);

  const isVideoUrl = (url?: string) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || lower.includes('blob:') || lower.startsWith('data:video/');
  };

  const handleSendMessage = useCallback(async (text: string, imageUrl?: string) => {
    if (!currentUser) return;
    
    const nm: ChatMessage = { 
      id: `USER-${Date.now()}`, 
      sender: 'user', 
      text, 
      imageUrl, 
      timestamp: new Date().toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' }) 
    };

    const success = await database.saveChatMessage(currentUser.email, currentUser.name, nm);
    
    if (!success) {
      console.error("Failed to save message to server");
      // Opcional: alert(language === 'en' ? "Failed to send message." : "Erro ao enviar mensagem.");
    }

    setChatThreads(prev => {
      const up = [...prev];
      const idx = up.findIndex(ct => ct.userEmail === currentUser.email);
      if (idx > -1) {
        up[idx] = { ...up[idx], messages: [...up[idx].messages, nm], lastUpdated: new Date().toISOString() };
      } else {
        up.push({ userEmail: currentUser.email, userName: currentUser.name, messages: [nm], lastUpdated: new Date().toISOString(), unreadCount: 1 });
      }
      return up;
    });
  }, [currentUser]);

  // Verify Stripe payment on return from Stripe Checkout & handle navigation/back-forward cache
  useEffect(() => {
    const resetCheckout = () => {
      setIsCheckingOut(false);
      setCheckoutStep(null);
      setCheckoutRedirectUrl(null);
    };

    // Listen for browser Back/Forward navigation or bfcache restoration
    const handlePageShow = () => {
      resetCheckout();
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('popstate', handlePageShow);

    const urlParams = new URLSearchParams(window.location.search);
    const stripeSuccess = urlParams.get('stripe_success');
    const stripeCancel = urlParams.get('stripe_cancel');
    const sessionId = urlParams.get('session_id');
    const orderId = urlParams.get('order_id');

    if (stripeCancel === 'true') {
      resetCheckout();
      window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
    }

    if (stripeSuccess === 'true' && orderId) {
      resetCheckout();
      const verifyPayment = async () => {
        setIsSaving(true);
        try {
          let res = await fetch(`/verify_stripe_session.php?session_id=${sessionId || ''}&order_id=${orderId}`);
          if (!res.ok) {
            res = await fetch(`/api/verify-stripe-session?session_id=${sessionId || ''}&order_id=${orderId}`);
          }
          const data = await res.json();
          if (data.status === 'success' && data.order) {
            setOrders(prev => {
              if (prev.some(o => o.id === data.order.id)) return prev;
              return [data.order, ...prev];
            });
            setCart([]);
            handleNavigate('my-orders');
            window.history.replaceState({}, document.title, window.location.pathname + '#my-orders');
            alert(language === 'en' 
              ? "Payment confirmed via Stripe! Your order is now sealed in your Order Sanctuary." 
              : "Pagamento confirmado via Stripe! Seu pedido foi selado no seu Santuário de Pedidos com sucesso.");
          }
        } catch (e) {
          console.error("Failed to verify Stripe payment", e);
        } finally {
          setIsSaving(false);
        }
      };
      verifyPayment();
    }

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('popstate', handlePageShow);
    };
  }, [language]);

  const handleCheckout = async () => {
    if (!currentUser) { 
      setIsCartOpen(false); 
      setIsUserAuthOpen(true); 
      return; 
    }

    if (cart.length === 0) return;
    
    setIsCheckingOut(true);
    setCheckoutStep('preparing');
    setCheckoutRedirectUrl(null);

    try {
      let response = await fetch('/create_stripe_session.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart,
          userId: currentUser.email,
          userName: currentUser.name,
          userEmail: currentUser.email,
          language: language,
          currency: currency
        })
      });

      if (!response.ok) {
        response = await fetch('/api/create-stripe-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: cart,
            userId: currentUser.email,
            userName: currentUser.name,
            userEmail: currentUser.email,
            language: language,
            currency: currency
          })
        });
      }

      const res = await response.json();

      if (res.status === 'success' && res.url) {
        setCheckoutStep('redirecting');
        setCheckoutRedirectUrl(res.url);
        setTimeout(() => {
          window.location.href = res.url;
        }, 1000);
      } else if (res.status === 'no_key') {
        const success = await database.saveOrder(res.order);
        setIsCheckingOut(false);
        setCheckoutStep(null);
        if (success) {
          setOrders(prev => [res.order, ...prev]);
          const orderSummary = cart.map(i => i.title[language] || i.title['en'] || '').join(', ');
          handleSendMessage(`🚀 NEW ORDER: #${res.order.id}. Items: ${orderSummary}.`);
          setCart([]);
          setIsCartOpen(false);
          handleNavigate('my-orders');
          alert(language === 'en'
            ? "Order placed successfully! Note: Configure STRIPE_SECRET_KEY in server environment or Admin Panel to activate live Stripe card checkout."
            : "Pedido registrado com sucesso! Nota: Para ser redirecionado para a tela de pagamento com cartão no Stripe, configure a chave STRIPE_SECRET_KEY no arquivo .env ou no Painel Admin.");
        }
      } else {
        setIsCheckingOut(false);
        setCheckoutStep(null);
        const errorMsg = res.message || (typeof res === 'string' ? res : JSON.stringify(res));
        alert(`Stripe Checkout Error: ${errorMsg}`);
      }
    } catch (e: any) {
      setIsCheckingOut(false);
      setCheckoutStep(null);
      alert(`Error connecting to server: ${e?.message || e}`);
    }
  };

  // Função centralizada para navegar e mudar a URL
  const handleNavigate = (page: string, keepFilters: boolean = false) => {
    const isReturningFromArt = activePage === 'art';
    if (selectedArt) {
      setSelectedArt(null);
    }
    setActivePage(page);
    if (page === 'gallery' && !isReturningFromArt && !keepFilters) {
      setCategoryFilter(ArtCategory.ANIMATED);
      setSubCategoryFilter(null);
      setArtTypeFilter(null);
    }
    try {
      const targetUrl = page === 'home' ? '/' : `/#${page}`;
      if (window.location.pathname.startsWith('/art/')) {
        window.history.pushState(null, '', targetUrl);
      } else {
        window.location.hash = page;
      }
    } catch (e) {
      // Silently fail if location.hash is inaccessible (e.g. in blob URLs)
      console.warn("Navigation warning: Unable to set location hash.", e);
    }
    if (!isReturningFromArt) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSelectArtFromCarousel = (art: ArtItem) => {
    previousPageRef.current = 'gallery';
    setCategoryFilter(art.category || ArtCategory.ANIMATED);
    setSubCategoryFilter(null);
    setArtTypeFilter(null);
    setSearchQuery('');
    setSelectedArt(art);
  };

  const filteredArts = useMemo(() => {
    const list = arts.filter(art => {
      const isAnimatedAll = categoryFilter === ArtCategory.ANIMATED;
      const matchesCategory = isAnimatedAll ? true : (categoryFilter ? art.category === categoryFilter : true);
      const matchesSubcategory = subCategoryFilter
        ? (art.subcategory?.toLowerCase() === subCategoryFilter.toLowerCase())
        : true;
      const matchesArtType = artTypeFilter
        ? (art.artType?.toLowerCase() === artTypeFilter.toLowerCase())
        : true;
      const title = art.title[language] || '';
      return matchesCategory && matchesSubcategory && matchesArtType && title.toLowerCase().includes(searchQuery.toLowerCase());
    });

    if (sortBy === 'price') {
      // Exclui artes 'not for sale' / sem preço válido para mostrar apenas obras disponíveis com preço
      const forSaleList = list.filter(art => !art.notForSale && (art.price || 0) > 0);
      return forSaleList.sort((a, b) => {
        const priceA = a.price || 0;
        const priceB = b.price || 0;
        return sortOrder === 'asc' ? priceA - priceB : priceB - priceA;
      });
    }

    if (sortBy === 'date') {
      return [...list].sort((a, b) => {
        // Extract timestamp if id is in format ART-123456789 or DEMO-1
        const getTime = (item: ArtItem) => {
          const id = item.id || '';
          if (id.startsWith('ART-')) {
            const num = parseInt(id.replace('ART-', ''), 10);
            return isNaN(num) ? 0 : num;
          }
          if (id.startsWith('DEMO-')) {
            const num = parseInt(id.replace('DEMO-', ''), 10);
            return isNaN(num) ? 0 : num * 1000;
          }
          const num = parseInt(id, 10);
          return isNaN(num) ? 0 : num;
        };
        const timeA = getTime(a);
        const timeB = getTime(b);
        return sortOrder === 'asc' ? timeA - timeB : timeB - timeA;
      });
    }

    return list;
  }, [arts, categoryFilter, subCategoryFilter, artTypeFilter, searchQuery, language, sortBy, sortOrder]);

  const ITEMS_PER_PAGE = 8;
  
  const paginatedArts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredArts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredArts, currentPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredArts.length / ITEMS_PER_PAGE);
  }, [filteredArts.length]);

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, subCategoryFilter, artTypeFilter, searchQuery, sortBy, sortOrder]);

  const handlePageChange = (pageNum: number) => {
    setCurrentPage(pageNum);
    setTimeout(() => {
      if (artsGridRef.current) {
        const elementPosition = artsGridRef.current.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - 110;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 50);
  };

  if (!isLoaded) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex flex-col items-center justify-center gap-12 z-[999]">
        <Flower className="w-20 h-20 text-[#c084fc] animate-spin" />
        <p className="text-white/80 font-bold uppercase tracking-[0.4em] text-[10px]">
          {language === 'en' ? 'Connecting to the Sanctuary...' : 'Conectando ao Santuário...'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent selection:bg-[#c084fc] selection:text-white relative">
      <Background activePage={activePage} />
      <MouseParticles />
      <SaveIndicator active={isSaving} language={language} />

      <Navbar 
        cartCount={cart.length} 
        onCartClick={() => currentUser ? setIsCartOpen(true) : setIsUserAuthOpen(true)}
        onChatClick={() => (currentUser ? setIsChatOpen(true) : setIsUserAuthOpen(true))}
        onAdminClick={() => setIsAdminLoginOpen(true)}
        onNavigate={handleNavigate}
        onUserAuthClick={() => setIsUserAuthOpen(true)}
        currentUser={currentUser}
        onLogout={() => { database.signOut(); setCurrentUser(null); handleNavigate('home'); }}
        activePage={activePage}
        language={language}
        currency={currency}
        onChangeLanguage={handleLanguageChange}
        onChangeCurrency={handleCurrencyChange}
      />

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-12 md:py-20 relative z-10">
        {activePage === 'home' && (
          <div className="space-y-40">
            <div className="flex flex-col items-center text-center space-y-8 py-4 md:py-6">
              <HeroCarousel 
                arts={arts.slice(0, 3)} 
                language={language} 
                onSelectArt={handleSelectArtFromCarousel} 
              />
              
              <button onClick={() => handleNavigate('gallery')} className="bg-white hover:bg-[#c084fc] text-black hover:text-white px-16 py-8 text-xs font-black uppercase tracking-[0.5em] transition-all flex items-center gap-8 group shadow-2xl admin-glow">
                {language === 'en' ? 'Enter Gallery' : 'Entrar na Galeria'} <ArrowRight className="w-6 h-6 group-hover:translate-x-4 transition-transform" />
              </button>
            </div>
            
            <CommentsSection 
              comments={comments}
              onAddComment={handleAddComment}
              currentUser={currentUser}
              language={language}
              onOpenAuth={() => setIsUserAuthOpen(true)}
            />

            <ProfileScreenshotsCarousel 
              screenshots={profileScreenshots} 
              language={language} 
            />

            <Footer settings={siteSettings} language={language} />
          </div>
        )}

        {(activePage === 'gallery' || activePage === 'art') && (
          <div className="space-y-8">
            <div className="glass-panel p-6 md:p-10 flex flex-col gap-8">
               <div className="flex items-center gap-4 text-zinc-400 text-[10px] font-black uppercase tracking-widest font-mono border-b border-purple-950/45 pb-4">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#9333ea] animate-pulse shadow-[0_0_10px_#9333ea]"></span>
                  {language === 'en' ? 'Categories' : 'Categorias'}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-4 sm:gap-6">
                 <button 
                   onClick={() => handleCategoryChange(null)} 
                   className="hidden"
                 >
                   <LayoutGrid className="absolute inset-0 w-8 h-8 m-auto text-white z-20" />
                   <span className="absolute bottom-2 inset-x-0 flex items-center justify-center text-[10px] font-black uppercase text-white z-20">All</span>
                 </button>
                 {Object.values(ArtCategory).map(cat => {
                   const isActive = categoryFilter === cat;
                   return (
                     <button 
                       key={cat} 
                       onClick={() => handleCategoryChange(cat)} 
                       className={`relative aspect-[9/14] border-2 transition-all duration-300 overflow-hidden rounded-md group ${
                         isActive 
                           ? 'border-[#9333ea] scale-105 shadow-[0_0_20px_rgba(147,51,234,0.5)]' 
                           : 'border-white/10 hover:border-[#9333ea]/50 opacity-70 hover:opacity-100'
                       }`}
                     >
                       <div className={`absolute inset-0 transition-all duration-300 ${isActive ? 'bg-[#7c3aed]/20' : 'bg-black/60 group-hover:bg-[#7c3aed]/10'}`} />
                       
                       {isVideoUrl(categoryCovers[cat] || DEFAULT_CATEGORY_COVERS[cat]) ? (
                         <video 
                           src={categoryCovers[cat] || DEFAULT_CATEGORY_COVERS[cat]} 
                           autoPlay loop muted 
                           className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${isActive ? 'opacity-40' : 'opacity-20 group-hover:opacity-30'}`} 
                         />
                       ) : (
                         <img 
                           src={categoryCovers[cat] || DEFAULT_CATEGORY_COVERS[cat]} 
                           className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${isActive ? 'opacity-40' : 'opacity-20 group-hover:opacity-30'}`} 
                         />
                       )}
                       
                       <div className={`absolute inset-0 bg-gradient-to-t from-[#5b21b6]/80 via-transparent to-transparent pointer-events-none z-10 transition-opacity duration-300 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'}`} />
                       
                       <span className="absolute inset-0 flex items-center justify-center text-[10px] font-black uppercase text-white z-20 text-center px-2 tracking-wider drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] group-hover:scale-105 transition-transform">
                         {cat}
                       </span>
                     </button>
                   );
                 })}
                </div>

               {categoryFilter && ART_SUBCATEGORIES[categoryFilter] && (
                 <div ref={subCategoriesRef} className="border-t border-white/5 pt-6 flex flex-col gap-4 animate-in fade-in slide-in-from-top-4 duration-500">
                   <div className="flex items-center gap-4 text-zinc-500 text-[10px] font-black uppercase tracking-widest font-mono">
                     <span className="w-2 h-2 rounded-full bg-[#9333ea] animate-pulse shadow-[0_0_8px_#9333ea]"></span>
                     {language === 'en' ? 'Subcategories' : 'Subcategorias'}
                   </div>
                   <div className="flex flex-wrap gap-3">
                     {[
                       { id: null, label: { pt: 'Todas', en: 'All' } },
                       ...ART_SUBCATEGORIES[categoryFilter]
                     ].map(sub => {
                       const isActive = subCategoryFilter === sub.id;
                       return (
                         <button
                           key={sub.id || 'all'}
                           onClick={() => handleSubCategoryChange(sub.id)}
                           className={`px-6 py-2.5 text-[10px] font-black uppercase tracking-widest border transition-all cursor-pointer duration-300 transform hover:scale-105 active:scale-95 flex items-center gap-2.5 ${
                             isActive 
                               ? 'bg-[#9333ea] text-white border-[#9333ea] font-black shadow-[0_0_15px_rgba(147,51,234,0.4)]' 
                               : 'bg-black/40 text-zinc-400 border-white/5 hover:border-[#9333ea]/50 hover:text-white'
                           }`}
                         >
                           {sub.id && (
                             <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-black' : 'bg-zinc-600'}`}></span>
                           )}
                           {sub.label[language]}
                         </button>
                       );
                     })}
                   </div>
                 </div>
               )}

               {categoryFilter && (
                 <div className="border-t border-white/5 pt-6 flex flex-col gap-4 animate-in fade-in slide-in-from-top-4 duration-500">
                   <div className="flex items-center gap-4 text-zinc-500 text-[10px] font-black uppercase tracking-widest font-mono">
                     <span className="w-2 h-2 rounded-full bg-[#9333ea] animate-pulse shadow-[0_0_8px_#9333ea]"></span>
                     {language === 'en' ? 'Art Type' : 'Tipo de Arte'}
                   </div>
                   <div className="flex flex-wrap gap-3">
                     {[
                       { id: null, label: { pt: 'Todas', en: 'All' } },
                       ...ART_TYPES
                     ].map(typeItem => {
                       const isActive = artTypeFilter === typeItem.id;
                       return (
                         <button
                           key={typeItem.id || 'all-types'}
                           onClick={() => handleArtTypeChange(typeItem.id)}
                           className={`px-6 py-2.5 text-[10px] font-black uppercase tracking-widest border transition-all cursor-pointer duration-300 transform hover:scale-105 active:scale-95 flex items-center gap-2.5 ${
                             isActive 
                               ? 'bg-[#9333ea] text-white border-[#9333ea] font-black shadow-[0_0_15px_rgba(147,51,234,0.4)]' 
                               : 'bg-black/40 text-zinc-400 border-white/5 hover:border-[#9333ea]/50 hover:text-white'
                           }`}
                         >
                           {typeItem.id && (
                             <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-black' : 'bg-zinc-600'}`}></span>
                           )}
                           {typeItem.label[language]}
                         </button>
                       );
                     })}
                   </div>
                 </div>
               )}

            </div>

            {/* Filter and Search Bar Section directly above the artworks grid */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-1 pb-2 bg-transparent">
              {/* Search Bar by Name (With violet outline/border) */}
              <div className="relative max-w-xs sm:w-64 group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-purple-400/70 group-hover:text-purple-300 transition-colors" />
                <input 
                  type="text" 
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)} 
                  placeholder={language === 'en' ? "Search artwork..." : "Buscar arte..."} 
                  className="w-full bg-transparent border border-purple-900/40 hover:border-purple-500/50 focus:border-purple-500 py-2 pl-9 pr-8 text-xs text-purple-200 placeholder-purple-400/40 outline-none focus:text-white transition-all font-medium rounded-none shadow-[0_0_10px_rgba(168,85,247,0.1)] focus:shadow-[0_0_15px_rgba(168,85,247,0.25)]" 
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-purple-400/70 hover:text-purple-200 p-1"
                    title={language === 'en' ? 'Clear search' : 'Limpar busca'}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>

              {/* Transparent Violet Filters for Price & Recent Uploads */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Recent / Recentes Filter */}
                <div className="flex items-center">
                  <button
                    onClick={() => {
                      if (sortBy !== 'date') {
                        setSortBy('date');
                        setSortOrder('desc');
                      } else {
                        setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
                      }
                    }}
                    className={`px-3.5 py-2 text-[11px] font-black uppercase tracking-wider border transition-all flex items-center gap-2 ${
                      sortBy === 'date'
                        ? 'bg-purple-950/30 text-purple-300 border-purple-500/80 shadow-[0_0_15px_rgba(168,85,247,0.35)]'
                        : 'bg-transparent text-purple-300/60 border-purple-900/30 hover:border-purple-500/50 hover:text-purple-200 hover:bg-purple-950/10'
                    }`}
                  >
                    <Calendar className="w-3.5 h-3.5 text-purple-400" />
                    <span>{language === 'en' ? 'Recent' : 'Recentes'}</span>
                    {sortBy === 'date' && (
                      sortOrder === 'desc' 
                        ? <ArrowDown className="w-3 h-3 text-purple-400" /> 
                        : <ArrowUp className="w-3 h-3 text-purple-400" />
                    )}
                  </button>
                </div>

                {/* Price Filter */}
                <div className="flex items-center">
                  <button
                    onClick={() => {
                      if (sortBy !== 'price') {
                        setSortBy('price');
                        setSortOrder('asc');
                      } else {
                        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                      }
                    }}
                    className={`px-3.5 py-2 text-[11px] font-black uppercase tracking-wider border transition-all flex items-center gap-2 ${
                      sortBy === 'price'
                        ? 'bg-purple-950/30 text-purple-300 border-purple-500/80 shadow-[0_0_15px_rgba(168,85,247,0.35)]'
                        : 'bg-transparent text-purple-300/60 border-purple-900/30 hover:border-purple-500/50 hover:text-purple-200 hover:bg-purple-950/10'
                    }`}
                  >
                    <DollarSign className="w-3.5 h-3.5 text-purple-400" />
                    <span>{language === 'en' ? 'Price' : 'Preço'}</span>
                    {sortBy === 'price' && (
                      sortOrder === 'asc' 
                        ? <span className="text-[9px] font-mono text-purple-400 font-bold">({language === 'en' ? 'Low to High' : 'Menor'})</span> 
                        : <span className="text-[9px] font-mono text-purple-400 font-bold">({language === 'en' ? 'High to Low' : 'Maior'})</span>
                    )}
                  </button>
                </div>

                {/* Reset Sorting Button (if active) */}
                {sortBy !== 'default' && (
                  <button
                    onClick={() => {
                      setSortBy('default');
                      setSortOrder('desc');
                    }}
                    className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-purple-400/60 hover:text-purple-200 bg-transparent border border-purple-900/30 hover:border-purple-500/40 hover:bg-purple-950/20 transition-all flex items-center gap-1.5"
                    title={language === 'en' ? 'Reset sorting' : 'Limpar ordenação'}
                  >
                    <RefreshCw className="w-3 h-3 text-purple-400" />
                    <span>{language === 'en' ? 'Reset' : 'Padrão'}</span>
                  </button>
                )}
              </div>
            </div>

            <div ref={artsGridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 md:gap-12">
              {paginatedArts.map(art => (
                <ArtCard key={art.id} item={art} onAddToCart={setSelectedArt} onPreview={setSelectedArt} language={language} currency={currency} discountPercentage={activeDiscount} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-white/5 pt-12 animate-in fade-in duration-500">
                <span className="text-zinc-500 font-mono text-xs uppercase tracking-wider">
                  {language === 'en' 
                    ? `Showing ${Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, filteredArts.length)}-${Math.min(currentPage * ITEMS_PER_PAGE, filteredArts.length)} of ${filteredArts.length} artworks`
                    : `Mostrando ${Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, filteredArts.length)}-${Math.min(currentPage * ITEMS_PER_PAGE, filteredArts.length)} de ${filteredArts.length} artes`}
                </span>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-10 h-10 border border-white/10 hover:border-[#c084fc] bg-black/40 hover:bg-[#c084fc]/10 flex items-center justify-center text-white disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:border-white/10 transition-all group animate-all hover:scale-105 duration-300"
                    title={language === 'en' ? 'Previous Page' : 'Página Anterior'}
                  >
                    <ChevronLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
                  </button>

                  <div className="flex items-center gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                      const idVal = `page-btn-${pageNum}`;
                      return (
                        <button
                          id={idVal}
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 font-bold font-mono text-xs transition-all border hover:scale-105 duration-300 ${
                            currentPage === pageNum
                              ? 'bg-[#c084fc] text-black border-[#c084fc] font-black shadow-[0_0_15px_rgba(192,132,252,0.4)]'
                              : 'bg-black/40 text-zinc-400 border-white/5 hover:border-[#9333ea]/50 hover:text-white'
                          }`}
                        >
                          {String(pageNum).padStart(2, '0')}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 border border-white/10 hover:border-[#c084fc] bg-black/40 hover:bg-[#c084fc]/10 flex items-center justify-center text-white disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:border-white/10 transition-all group animate-all hover:scale-105 duration-300"
                    title={language === 'en' ? 'Next Page' : 'Próxima Página'}
                  >
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activePage === 'custom-art' && (
          <CustomRequest 
            language={language} 
            onOpenChat={() => currentUser ? setIsChatOpen(true) : setIsUserAuthOpen(true)} 
            onPreview={setSelectedArt}
            commissionTiers={commissionTiers}
            settings={siteSettings}
          />
        )}

        {activePage === 'faq' && (
          <FaqSection 
            settings={siteSettings} 
            language={language} 
            onOpenChat={() => currentUser ? setIsChatOpen(true) : setIsUserAuthOpen(true)}
          />
        )}
        
        {activePage === 'my-orders' && currentUser && <UserOrders orders={orders.filter(o => o.userEmail.toLowerCase() === currentUser?.email.toLowerCase())} language={language} arts={arts} />}
        {activePage === 'profile' && currentUser && <UserProfile user={currentUser} orders={orders.filter(o => o.userEmail.toLowerCase() === currentUser.email.toLowerCase())} language={language} arts={arts} onUpdateUser={async (d) => { await database.updateUser(d); setCurrentUser(prev => prev ? {...prev, ...d} : null); return true; }} onLogout={() => { database.signOut(); setCurrentUser(null); handleNavigate('home'); }} />}
        
      </main>

      {selectedArt && (
        <ImageModal 
          item={selectedArt} 
          onClose={() => {
            setSelectedArt(null);
            if (activePage === 'art') {
              const returnPage = previousPageRef.current || 'gallery';
              handleNavigate(returnPage, true);
            }
          }}
          onAddToCart={(it, sig, disc, steam, font, df, px, py, sc, cl, notes) => {
            if (!currentUser) { setIsUserAuthOpen(true); return; }
            setCart(prev => [...prev, { ...it, quantity: 1, customSignature: sig, discord: disc, steamUrl: steam, customFont: font, dafontLink: df, sigPosX: px, py, sigScale: sc, sigColor: cl, notes }]);
            setIsCartOpen(true);
          }}
          language={language}
          currency={currency}
          discountPercentage={activeDiscount}
        />
      )}

      {isCartOpen && currentUser && (
        <div className="fixed inset-0 z-[1000] flex justify-end animate-in fade-in duration-500">
          <div className="absolute inset-0 bg-black/95 backdrop-blur-md" onClick={() => !isCheckingOut && setIsCartOpen(false)} />
          <div className="relative w-full max-w-md bg-zinc-950 border-l border-white/5 h-full flex flex-col p-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-black text-white uppercase">
                {language === 'en' ? 'Cart' : language === 'pt' ? 'Carrinho' : language === 'es' ? 'Carrito' : 'Корзина'}
              </h2>
              <button 
                onClick={() => !isCheckingOut && setIsCartOpen(false)} 
                className="text-zinc-500 hover:text-white p-2 rounded-full hover:bg-white/5 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-4 custom-scrollbar pr-1">
              {cart.map((item, idx) => (
                <div key={idx} className="flex gap-4 p-4 bg-zinc-900 border border-white/5">
                   <div className="w-12 h-12 bg-black flex-shrink-0"><img src={item.imageUrl} className="w-full h-full object-cover" /></div>
                   <div className="flex-1 min-w-0">
                     <h4 className="text-white text-[10px] uppercase font-black truncate">{item.title[language] || item.title['en']}</h4>
                     <p className="text-[#c084fc] font-mono text-xs">{formatCurrency(item.price, currency)}</p>
                   </div>
                   <button onClick={() => !isCheckingOut && setCart(prev => prev.filter((_, i) => i !== idx))} className="text-zinc-600 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="pt-8 border-t border-white/5">
               <div className="flex justify-between mb-8">
                 <span className="text-zinc-500 uppercase text-[10px] font-black">Total</span>
                 <span className="text-white text-2xl font-black">
                   {formatCurrency(cart.reduce((a, b) => a + (b.price * b.quantity), 0), currency)}
                 </span>
               </div>
               <button 
                 onClick={handleCheckout} 
                 disabled={cart.length === 0 || isCheckingOut} 
                 className="w-full bg-white hover:bg-[#c084fc] text-black hover:text-white py-5 font-black uppercase text-xs transition-all shadow-xl active:scale-98 flex items-center justify-center gap-2 disabled:opacity-50"
               >
                 {isCheckingOut ? (
                   <>
                     <Loader2 className="w-4 h-4 animate-spin text-current" />
                     <span>{language === 'pt' ? 'Processando...' : 'Processing...'}</span>
                   </>
                 ) : (
                   <span>{language === 'en' ? 'Checkout' : language === 'pt' ? 'Finalizar Pedido' : language === 'es' ? 'Finalizar Pedido' : 'Оформить Заказ'}</span>
                 )}
               </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation & Redirection Overlay */}
      {isCheckingOut && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-300">
          <div 
            className="absolute inset-0" 
            onClick={() => {
              setIsCheckingOut(false);
              setCheckoutStep(null);
              setCheckoutRedirectUrl(null);
            }} 
          />
          <div className="relative w-full max-w-md bg-[#0b021d] border-2 border-[#c084fc]/70 p-6 md:p-8 rounded-2xl shadow-[0_0_60px_rgba(168,85,247,0.5)] text-center select-none flex flex-col items-center animate-in zoom-in-95 duration-300 z-10">
            
            {/* Close Button */}
            <button
              onClick={() => {
                setIsCheckingOut(false);
                setCheckoutStep(null);
                setCheckoutRedirectUrl(null);
              }}
              className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-full transition-all"
              title="Fechar / Cancelar"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Glowing Icon Container */}
            <div className="relative mb-5">
              <div className="absolute inset-0 bg-[#c084fc]/30 rounded-full blur-xl animate-pulse"></div>
              <div className="relative w-16 h-16 rounded-full bg-purple-950/80 border border-purple-500/50 flex items-center justify-center text-[#c084fc] shadow-inner">
                {checkoutStep === 'redirecting' ? (
                  <ShieldCheck className="w-8 h-8 text-green-400 animate-pulse" />
                ) : (
                  <Loader2 className="w-8 h-8 text-[#c084fc] animate-spin" />
                )}
              </div>
            </div>

            {/* Title */}
            <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight mb-2 oil-text">
              {language === 'pt' 
                ? (checkoutStep === 'redirecting' ? 'Redirecionando para o Stripe...' : 'Preparando Checkout Seguro...') 
                : language === 'es'
                ? (checkoutStep === 'redirecting' ? 'Redirigiendo a Stripe...' : 'Preparando Pago Seguro...')
                : language === 'ru'
                ? (checkoutStep === 'redirecting' ? 'Перенаправление в Stripe...' : 'Подготовка Оплаты...')
                : (checkoutStep === 'redirecting' ? 'Redirecting to Stripe...' : 'Preparing Secure Checkout...')}
            </h3>

            {/* Description */}
            <p className="text-xs text-purple-200/90 font-medium max-w-sm mb-6 leading-relaxed">
              {language === 'pt'
                ? (checkoutStep === 'redirecting' 
                    ? 'Sua sessão criptografada foi criada! Você está sendo redirecionado para a página de pagamento seguro do Stripe.'
                    : 'Aguarde um instante enquanto preparamos seu link oficial e criptografado de pagamento...')
                : language === 'es'
                ? (checkoutStep === 'redirecting'
                    ? '¡Tu sesión cifrada se ha creado! Serás transferido a la página de pago oficial de Stripe.'
                    : 'Espera un momento mientras generamos tu enlace de pago seguro...')
                : language === 'ru'
                ? (checkoutStep === 'redirecting'
                    ? 'Ваша сессия оплаты создана! Перенаправляем на защищенный шлюз Stripe.'
                    : 'Пожалуйста, подождите, подготавливаем защищенную сессию оплаты...')
                : (checkoutStep === 'redirecting'
                    ? 'Your encrypted session is ready! You are being redirected to the official Stripe payment portal.'
                    : 'Please wait a moment while we generate your secure payment link...')}
            </p>

            {/* Animated Progress Bar */}
            <div className="w-full bg-purple-950/80 border border-purple-800/40 h-2 rounded-full overflow-hidden mb-5">
              <div className={`h-full bg-gradient-to-r from-purple-500 via-[#c084fc] to-white transition-all duration-700 ${checkoutStep === 'redirecting' ? 'w-full' : 'w-2/3 animate-pulse'}`}></div>
            </div>

            {/* Security Badge */}
            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-purple-300/80 bg-purple-950/60 px-3 py-1.5 rounded-full border border-purple-800/40">
              <Lock className="w-3 h-3 text-green-400" />
              <span>SSL 256-bit Encrypted • Stripe Verified</span>
            </div>

            {/* Fallback direct click */}
            {checkoutRedirectUrl && (
              <a
                href={checkoutRedirectUrl}
                className="mt-4 text-[10px] font-mono text-[#c084fc] hover:text-white underline tracking-wider uppercase transition-colors"
              >
                {language === 'pt' ? 'Clique aqui se não for redirecionado automaticamente' : 'Click here if not redirected automatically'}
              </a>
            )}

            {/* Cancel / Dismiss Button */}
            <button
              onClick={() => {
                setIsCheckingOut(false);
                setCheckoutStep(null);
                setCheckoutRedirectUrl(null);
              }}
              className="mt-4 text-[11px] font-bold text-zinc-400 hover:text-white py-1.5 px-4 rounded-lg hover:bg-white/5 transition-all"
            >
              {language === 'pt' ? 'Cancelar e Voltar' : language === 'es' ? 'Cancelar y Volver' : language === 'ru' ? 'Отмена и назад' : 'Cancel and Return'}
            </button>
          </div>
        </div>
      )}

      {isUserAuthOpen && <UserAuthModal onClose={() => setIsUserAuthOpen(false)} onLoginSuccess={(u) => { setCurrentUser(u); setIsUserAuthOpen(false); }} language={language} />}
      {isAdminLoginOpen && <AdminLogin onClose={() => setIsAdminLoginOpen(false)} onLogin={() => { setIsAdminLoginOpen(false); setIsAdminPanelOpen(true); }} />}
      {isAdminPanelOpen && (
        <AdminPanel 
          arts={arts} 
          orders={orders} 
          chatThreads={chatThreads}
          comments={comments}
          categoryCovers={categoryCovers}
          commissionTiers={commissionTiers}
          siteSettings={siteSettings}
          profileScreenshots={profileScreenshots}
          onUpdateArts={(u) => { setArts(prev => { const n = typeof u === 'function' ? u(prev) : u; storage.save('arts', n); return n; }); }}
          onUpdateOrders={setOrders}
          onUpdateSupport={() => {}}
          onUpdateChatThreads={setChatThreads}
          onUpdateComments={setComments}
          onUpdateCategoryCovers={setCategoryCovers}
          onUpdateCommissionTiers={setCommissionTiers}
          onUpdateSiteSettings={(newSettings) => {
            setSiteSettings(newSettings);
            storage.save('settings', newSettings, 'registry');
            try {
              localStorage.setItem('niiko_site_settings_backup', JSON.stringify(newSettings));
            } catch (e) {}
          }}
          onUpdateProfileScreenshots={setProfileScreenshots}
          onClose={() => setIsAdminPanelOpen(false)}
          language={language}
          notifications={notifications}
          onClearNotifications={(type) => setNotifications(prev => ({ ...prev, [type]: 0 }))}
        />
      )}
      {isChatOpen && currentUser && <ChatOverlay userName={currentUser.name} messages={chatThreads.find(t => t.userEmail === currentUser.email)?.messages || []} onSendMessage={handleSendMessage} onClose={() => setIsChatOpen(false)} language={language} />}
    </div>
  );
};

export default App;
